import {useCallback, useEffect, useState} from 'react';

export function toDateString(isoString) {
  if (!isoString) {
    return '';
  }
  const date = new Date(isoString);
  const year = date.getUTCFullYear().toString();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function toISOString(dateString) {
  if (!dateString) {
    return '';
  }
  const [year, month, day] = dateString.split('-');
  return new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day)),
  ).toISOString();
}

export function updatedSearchParams(searchParams, newParams) {
  const params = {};
  for (const pair of searchParams.entries()) {
    if (pair[1]) {
      params[pair[0]] = pair[1];
    }
  }
  for (const pair of Object.entries(newParams)) {
    if (pair[1]) {
      params[pair[0]] = pair[1];
    } else {
      delete params[pair[0]];
    }
  }
  return params;
}

export function formatDate(datestring) {
  const date = new Date(datestring);
  const yyyy = date.getUTCFullYear().toString();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const isMac = /mac/i.test(
  navigator.userAgentData
    ? navigator.userAgentData.platform
    : navigator.platform,
);

/**
 * @param {KeyboardEvent} event The event.
 * @return {boolean} Cmd on a Mac or Ctrl on other.
 */
export function cmdOrCtrl(event) {
  if (isMac) {
    return event.metaKey;
  }
  return event.ctrlKey;
}

const storageKey = 'publicat-preferences';

function getStoredPreference(key, defaultValue) {
  const serializedPreferences = localStorage.getItem(storageKey);
  if (!serializedPreferences) {
    return defaultValue;
  }

  const preferences = JSON.parse(serializedPreferences);
  if (!(key in preferences)) {
    return defaultValue;
  }

  return preferences[key];
}

function setStoredPreference(key, value) {
  const serializedPreferences = localStorage.getItem(storageKey);
  const preferences = serializedPreferences
    ? JSON.parse(serializedPreferences)
    : {};
  preferences[key] = value;
  localStorage.setItem(storageKey, JSON.stringify(preferences));
}

export function usePreference(key, initialValue) {
  const [preference, setPreferenceInternal] = useState(
    getStoredPreference(key, initialValue),
  );

  const setPreference = useCallback(
    newValue => {
      setStoredPreference(key, newValue);
      setPreferenceInternal(newValue);
    },
    [key],
  );

  const handleStorageUpdate = useCallback(
    event => {
      if (event.key !== storageKey) {
        return;
      }
      const updatedPreference = getStoredPreference(key, preference);
      if (preference !== updatedPreference) {
        setPreferenceInternal(updatedPreference);
      }
    },
    [key, preference],
  );

  useEffect(() => {
    const listener = handleStorageUpdate;
    addEventListener('storage', listener);
    return () => {
      removeEventListener('storage', listener);
    };
  }, [handleStorageUpdate]);

  return [preference, setPreference];
}

export function cleanPublication(data) {
  // remove empty authors
  const authors = data.authors.filter(
    author =>
      !!author.name.trim() ||
      !!author.email.trim() ||
      !!author.affiliation.trim(),
  );

  // strip links with no rel or href
  const links = data.links.filter(link => !!link.rel && !!link.href);

  if (!data.geometry) {
    // avoid geometry: null
    delete data.geometry;
  }

  return {...data, authors, links};
}

/**
 * @param {URL} url A url.
 * @return {string} The path, query string, and any fragment.
 */
function stripOrigin(url) {
  const href = url.href;
  return href.replace(url.origin, '');
}

/**
 * Get a new search url.
 * @param {Object<string, string>|undefined} params The search params.
 * @return {string} The search URL.
 */
export function getNewSearchUrl(params) {
  const url = new URL('/', window.location.href);
  if (params) {
    for (const key in params) {
      const value = params[key];
      if (value) {
        url.searchParams.set(key, value);
      }
    }
  }
  return stripOrigin(url);
}

/**
 * Get a search url with an updated set of params.
 * @param {Object<string, string>} params The search params.
 * @return {string} The search URL.
 */
export function getUpdatedSearchUrl(params) {
  const url = new URL(window.location.href);
  url.pathname = '/';
  for (const key in params) {
    const value = params[key];
    if (!value) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value);
    }
  }
  return stripOrigin(url);
}

const hasSpace = /\s/;

/**
 * Quote a string if the original has a space.
 * @param {string} value A string.
 * @return {string} A potentially quoted string.
 */
export function quoteSpaced(value) {
  if (hasSpace.test(value)) {
    return '"' + value + '"';
  }
  return value;
}
